import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import BlogCard from '../../../components/gcstudent/blogcard/BlogCard';
import "../../../assets/css/gcstudent/Blog/BlogPage.css";

// ── Blog Detail ───────────────────────────────────────────────

const BlogDetail = ({ blog, onBack }) => (
  <div className="gcStudentBlogDetailPage">
    <Container>
      <div className="gcStudentBlogDetailInner">

        {/* ← Back */}
        <button className="gcStudentBlogBackLink" onClick={onBack}>
          <span className="gcStudentBlogBackArrow">&#8592;</span>
          <span className="gcStudentBlogBackText">Back to Blogs</span>
        </button>

        {/* Detail Card */}
        <div className="gcStudentBlogDetailCard">

          {/* Hero Image */}
          <div className="gcStudentDetailImgWrap">
            {blog.imgSrc ? (
              <img src={blog.imgSrc} alt={blog.imgAlt || blog.title} />
            ) : (
              <div className="gcStudentDetailImgPlaceholder">
                {blog.imgAlt || blog.title}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="gcStudentDetailContent">
            <span className="gcStudentDetailBadge">{blog.category}</span>

            <h1 className="gcStudentDetailTitle">{blog.title}</h1>

            <div className="gcStudentDetailMetaRow">
              <span className="gcStudentDetailMetaItem">
                <span className="gcStudentMetaIcon">&#9675;</span>
                {blog.author}
              </span>
              <span className="gcStudentDetailMetaItem">
                <span className="gcStudentMetaIcon">&#128197;</span>
                {blog.date}
              </span>
            </div>

            <hr className="gcStudentDetailDivider" />

            {/* Excerpt as intro */}
            {blog.excerpt && (
              <p className="gcStudentDetailIntro">{blog.excerpt}</p>
            )}

            {/* Rich text HTML content from backend */}
            {blog.content ? (
              <div
                className="gcStudentDetailBody gcStudentDetailBody--html"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            ) : (
              blog.body && blog.body.map((para, idx) => (
                <p className="gcStudentDetailBody" key={idx}>{para}</p>
              ))
            )}

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="gcStudentDetailTags">
                {blog.tags.map((tag) => (
                  <span key={tag} className="gcStudentDetailTag">#{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </Container>
  </div>
);

// ── Skeleton Card ─────────────────────────────────────────────

const SkeletonCard = () => (
  <Col md={4} className="d-flex">
    <div className="gcStudentBlogCard gcStudentBlogCard--skeleton">
      <div className="gcStudentCardImgWrap gcStudentSkeleton__img" />
      <div className="gcStudentCardBody">
        <div className="gcStudentSkeleton__line gcStudentSkeleton__line--xs" />
        <div className="gcStudentSkeleton__line" />
        <div className="gcStudentSkeleton__line gcStudentSkeleton__line--md" />
        <div className="gcStudentSkeleton__line gcStudentSkeleton__line--sm" />
      </div>
    </div>
  </Col>
);

// ── Main Blog Page ────────────────────────────────────────────

const BlogPage = () => {
  const { baseUrl }                     = useContext(AppContext);
  const location                        = useLocation();
  const [blogs,        setBlogs]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [search,       setSearch]       = useState('');

  /* ── Fetch blogs ── */
  const fetchBlogs = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${baseUrl}/general/blogs/fetchAllBlogs`)
      .then((res) => {
        if (res.data.success === true) {
          const mapped = res.data.blogs.map((b) => ({
            id:       b.id,
            title:    b.title    || '',
            author:   b.author   || '',
            category: b.category || 'General',
            date:     b.created_at
                        ? new Date(b.created_at).toLocaleDateString('en-PK', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })
                        : '',
            excerpt:  b.excerpt  || '',
            content:  b.content  || '',
            tags:     Array.isArray(b.tags)
                        ? b.tags
                        : (typeof b.tags === 'string'
                            ? JSON.parse(b.tags || '[]')
                            : []),
            imgSrc: b.img ? `${baseUrl.replace('/api', '')}/storage/${b.img}` : '',
            imgAlt:   b.title || '',
          }));
          setBlogs(mapped);

          // ── If navigated from home page with a specific blog, open it directly ──
          if (location.state?.blogId) {
            const match = mapped.find((bl) => bl.id === location.state.blogId);
            if (match) setSelectedBlog(match);
          }
        } else {
          setError('Failed to load blogs.');
        }
      })
      .catch((err) => {
        console.log(err);
        setError('Something went wrong. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBlogs(); }, []);

  /* ── Search filter ── */
  const filtered = blogs.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      b.author.toLowerCase().includes(search.toLowerCase()) ||
      b.category.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Detail view ── */
  if (selectedBlog) {
    return (
      <BlogDetail
        blog={selectedBlog}
        onBack={() => setSelectedBlog(null)}
      />
    );
  }

  return (
    <section className="gcStudentBlogPage">
      <Container>

        {/* ── Search ── */}
        <div className="gcStudentBlogSearchWrap">
          <svg className="gcStudentBlogSearchIcon" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            className="gcStudentBlogSearch"
            type="text"
            placeholder="Search blogs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="gcStudentBlogError">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{error}</span>
            <button onClick={fetchBlogs} className="gcStudentBlogRetryBtn">Retry</button>
          </div>
        )}

        {/* ── Grid — 3 per row ── */}
        <div className="gcStudentBlogGrid">
          <Row className="g-4">

            {/* Skeletons */}
            {loading && [1, 2, 3, 4, 5, 6].map((s) => <SkeletonCard key={s} />)}

            {/* Cards */}
            {!loading && filtered.map((blog) => (
              <Col key={blog.id} md={4} className="d-flex">
                <BlogCard
                  image={blog.imgSrc}
                  category={blog.category}
                  title={blog.title}
                  excerpt={blog.excerpt}
                  author={blog.author}
                  date={blog.date}
                  onClick={() => setSelectedBlog(blog)}
                />
              </Col>
            ))}

            {/* Empty */}
            {!loading && !error && filtered.length === 0 && (
              <Col xs={12}>
                <div className="gcStudentBlogEmpty">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                  <p>{search ? 'No blogs match your search.' : 'No blogs published yet.'}</p>
                </div>
              </Col>
            )}

          </Row>
        </div>

      </Container>
    </section>
  );
};

export default BlogPage;